/* ==========================================================================
   T3 header — mobile drawer, dropdowns, cart hand-off.

   Jump-free by construction:
   - sticky is CSS `position:sticky`, so the bar never leaves the flow and the
     page cannot shift when it pins;
   - the "scrolled" shadow is driven by an IntersectionObserver sentinel, not a
     scroll handler, so there is no per-frame work while scrolling;
   - locking the background uses `overflow:hidden` plus scrollbar-width padding.
     It never sets `position:fixed` on <body>, which is what loses the scroll
     position and snaps the page to the top when the drawer closes.
   ========================================================================== */

// Declared inside the guard so a second request for this file cannot throw
// "Identifier 'T3Header' has already been declared".
if (!customElements.get('t3-header')) {
  class T3Header extends HTMLElement {
  connectedCallback() {
    this.burger = this.querySelector('[data-t3-burger]');
    this.drawer = this.querySelector('[data-t3-drawer]');
    this.panel = this.querySelector('.t3-drawer__panel');

    this.bindDrawer();
    this.bindSticky();
    this.bindCart();
    this.bindCartCount();
    this.bindDropdowns();
  }

  /* --------------------------------------------------------------- drawer */

  bindDrawer() {
    if (!this.burger || !this.drawer) return;

    this.burger.addEventListener('click', () => this.toggleDrawer());

    this.drawer.querySelectorAll('[data-t3-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });

    // Escape closes; Tab is kept inside the panel while it is open.
    this.onKeydown = (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        this.closeDrawer();
      } else if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    };
    document.addEventListener('keydown', this.onKeydown);

    // Following a link should not leave the drawer open behind the new page.
    this.drawer.querySelectorAll('a[href]').forEach((a) => {
      a.addEventListener('click', () => this.closeDrawer());
    });

    // Leaving the mobile breakpoint with the drawer open would strand the lock.
    this.mq = window.matchMedia('(min-width: 990px)');
    this.onBreakpoint = (e) => {
      if (e.matches && this.isOpen) this.closeDrawer();
    };
    this.mq.addEventListener('change', this.onBreakpoint);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    this.mq?.removeEventListener('change', this.onBreakpoint);
    this.unlockScroll();
    this.stickObserver?.disconnect();
    this.cartUnsubscriber?.();
  }

  get isOpen() {
    return this.drawer && !this.drawer.hidden;
  }

  toggleDrawer() {
    this.isOpen ? this.closeDrawer() : this.openDrawer();
  }

  openDrawer() {
    this.drawer.hidden = false;
    // Next frame, so the transition has a start state to animate from.
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
    this.burger.setAttribute('aria-expanded', 'true');
    this.lockScroll();
    this.lastFocused = document.activeElement;
    this.panel?.querySelector('a, button')?.focus();
  }

  closeDrawer() {
    if (!this.isOpen) return;
    this.drawer.classList.remove('is-open');
    this.burger.setAttribute('aria-expanded', 'false');
    this.unlockScroll();

    const finish = () => {
      this.drawer.hidden = true;
    };
    this.drawer.addEventListener('transitionend', finish, { once: true });
    // Fallback in case the transition never fires (reduced motion, hidden tab).
    setTimeout(() => {
      if (!this.drawer.classList.contains('is-open')) this.drawer.hidden = true;
    }, 400);

    this.lastFocused?.focus?.();
  }

  trapFocus(e) {
    const focusables = this.panel?.querySelectorAll(
      'a[href], button:not([disabled]), summary, input, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables || focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  lockScroll() {
    const bar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (bar > 0) document.body.style.paddingRight = bar + 'px';
  }

  unlockScroll() {
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  }

  /* --------------------------------------------------------------- sticky */

  bindSticky() {
    if (this.dataset.sticky !== 'true') return;

    // The section wrapper is what sticks (see t3.css). No sentinel element:
    // <body> is a CSS grid, so an extra child would shift its rows. Instead the
    // root is shrunk by 1px at the top — once pinned at top:0 the wrapper pokes
    // into that strip and stops being fully visible.
    const host = this.closest('.shopify-section') || this;
    this.stickObserver = new IntersectionObserver(
      ([entry]) => this.classList.toggle('is-stuck', entry.intersectionRatio < 1),
      { rootMargin: '-1px 0px 0px 0px', threshold: [1] }
    );
    this.stickObserver.observe(host);
  }

  /* ----------------------------------------------------------------- cart */

  bindCart() {
    const cartLink = this.querySelector('[data-t3-cart]');
    if (!cartLink) return;

    cartLink.addEventListener('click', (e) => {
      const drawer = document.querySelector('cart-drawer');
      // Only intercept when Dawn's drawer is actually present and usable;
      // otherwise let the link go to /cart normally.
      if (!drawer || typeof drawer.open !== 'function') return;
      if (drawer.classList.contains('is-empty')) return;
      e.preventDefault();
      drawer.open(cartLink);
    });
  }

  /* ----------------------------------------------------------- cart count */

  // Dawn keeps its badge fresh by re-rendering the #cart-icon-bubble section.
  // This header draws its own badge and has no such element, which is exactly
  // what used to make cart.js throw mid-render (see the note there). The count
  // is driven off the cartUpdate event instead, so it stays right without a
  // page load. PUB_SUB_EVENTS is a top-level `const` in constants.js, so it is
  // not on window — test the bare identifier.
  bindCartCount() {
    this.countEl = this.querySelector('[data-t3-cart-count]');
    if (!this.countEl || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    this.cartUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      // /cart/add.js resolves with the line that was added, not the whole cart,
      // so on that path the count has to be read back.
      if (typeof count === 'number') this.setCartCount(count);
      else this.refreshCartCount();
    });
  }

  refreshCartCount() {
    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.json())
      .then((cart) => this.setCartCount(cart.item_count))
      .catch(() => {});
  }

  setCartCount(count) {
    if (!this.countEl || typeof count !== 'number') return;
    this.countEl.textContent = count;
    this.countEl.classList.toggle('is-empty', count === 0);
    this.querySelector('[data-t3-cart]')?.setAttribute('aria-label', `Cart, ${count} items`);
  }

  /* ------------------------------------------------------------ dropdowns */

  bindDropdowns() {
    const groups = this.querySelectorAll('.t3-nav__details');

    groups.forEach((d) => {
      // Only one open at a time.
      d.addEventListener('toggle', () => {
        if (!d.open) return;
        groups.forEach((other) => {
          if (other !== d) other.open = false;
        });
      });
    });

    document.addEventListener('click', (e) => {
      if (this.contains(e.target)) return;
      groups.forEach((d) => {
        d.open = false;
      });
    });
  }
}

  customElements.define('t3-header', T3Header);
}
